import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EmailConnectionCard from './components/EmailConnectionCard';
import EmailThreadItem from './components/EmailThreadItem';
import EmailComposer from './components/EmailComposer';
import EmailSearchBar from './components/EmailSearchBar';
import EmailStats from './components/EmailStats';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';




const EmailsPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [connectedAccounts, setConnectedAccounts] = useState({
    gmail: false,
    outlook: false
  });

  // Email data will come from email integration in future
  const mockEmails = [];

  // Mock email statistics
  const emailStats = {
    total: mockEmails?.length,
    unread: mockEmails?.filter(email => !email?.isRead)?.length,
    today: mockEmails?.filter(email => {
      const today = new Date();
      const emailDate = new Date(email.date);
      return emailDate?.toDateString() === today?.toDateString();
    })?.length,
    connectedAccounts: Object.values(connectedAccounts)?.filter(Boolean)?.length
  };

  // Filter emails based on search and filters
  const filteredEmails = mockEmails?.filter(email => {
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      if (!email?.subject?.toLowerCase()?.includes(query) && 
          !email?.sender?.toLowerCase()?.includes(query) && 
          !email?.preview?.toLowerCase()?.includes(query)) {
        return false;
      }
    }

    if (filters?.sender && !email?.sender?.toLowerCase()?.includes(filters?.sender?.toLowerCase())) {
      return false;
    }

    if (filters?.account && !email?.account?.toLowerCase()?.includes(filters?.account?.toLowerCase())) {
      return false;
    }

    if (filters?.isRead === 'read' && !email?.isRead) {
      return false;
    }

    if (filters?.isRead === 'unread' && email?.isRead) {
      return false;
    }

    if (filters?.dateRange) {
      const now = new Date();
      const emailDate = new Date(email.date);
      
      switch (filters?.dateRange) {
        case 'today':
          if (emailDate?.toDateString() !== now?.toDateString()) return false;
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (emailDate < weekAgo) return false;
          break;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (emailDate < monthAgo) return false;
          break;
        case 'quarter':
          const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          if (emailDate < quarterAgo) return false;
          break;
      }
    }

    return true;
  });

  const handleEmailClick = (email) => {
    setSelectedEmail(email);
  };

  const handleSendEmail = (emailData) => {
    console.log('Sending email:', emailData);
    // In a real app, this would send the email via API
  };

  const handleConnectProvider = (provider) => {
    setConnectedAccounts(prev => ({
      ...prev,
      [provider]: true
    }));
    console.log(`Connecting to ${provider}...`);
    // In a real app, this would initiate OAuth flow
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  // Close sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, []);

  const hasConnectedAccounts = Object.values(connectedAccounts)?.some(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <Header onToggleSidebar={handleSidebarToggle} isSidebarOpen={isSidebarOpen} />
      <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
      <main className="lg:ml-64 pt-16">
        <div className="h-[calc(100vh-4rem)] flex flex-col">
          {/* Page Header */}
          <div className="bg-background border-b border-border p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Emails</h1>
                <p className="text-muted-foreground mt-1">
                  Manage your email communications and integrations
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/integrations')}
                >
                  <Icon name="Settings" size={16} className="mr-2" />
                  Manage Integrations
                </Button>
                
                <Button
                  variant="default"
                  onClick={() => setIsComposerOpen(true)}
                  disabled={!hasConnectedAccounts}
                >
                  <Icon name="Plus" size={16} className="mr-2" />
                  Compose Email
                </Button>
              </div>
            </div>
          </div>

          {!hasConnectedAccounts ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="max-w-4xl w-full">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Mail" size={32} className="text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    Connect Your Email Accounts
                  </h2>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Integrate with Gmail or Outlook to manage all your customer communications in one place
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <EmailConnectionCard
                    provider="Gmail"
                    icon="Mail"
                    description="Connect your Gmail account to sync emails, manage threads, and compose messages directly from LocalProCRM."
                    onConnect={() => handleConnectProvider('gmail')}
                    isConnected={connectedAccounts?.gmail}
                  />
                  
                  <EmailConnectionCard
                    provider="Outlook"
                    icon="Mail"
                    description="Integrate with Microsoft Outlook to access your emails, calendar, and contacts seamlessly."
                    onConnect={() => handleConnectProvider('outlook')}
                    isConnected={connectedAccounts?.outlook}
                  />
                </div>

                <div className="mt-8 p-6 bg-muted/30 rounded-xl border border-border">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Shield" size={16} className="text-accent-foreground" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">Secure Integration</h3>
                      <p className="text-sm text-muted-foreground">
                        Your email data is encrypted and secure. We only access what's necessary to provide CRM functionality. 
                        You can disconnect at any time from the integrations settings.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <EmailStats stats={emailStats} />
              <EmailSearchBar onSearch={setSearchQuery} onFilterChange={setFilters} />
              <div className="flex-1 flex overflow-hidden">
                {/* Email List */}
                <div className="w-full lg:w-1/2 border-r border-border overflow-y-auto">
                  {filteredEmails?.length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">No emails found</h3>
                        <p className="text-muted-foreground">
                          {searchQuery || Object.keys(filters)?.length > 0
                            ? "Try adjusting your search or filters" :"Your emails will appear here once you connect an account"
                          }
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {filteredEmails?.map((email) => (
                        <EmailThreadItem
                          key={email?.id}
                          email={email}
                          onClick={handleEmailClick}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Email Detail View */}
                <div className="hidden lg:block lg:w-1/2 bg-muted/20">
                  {selectedEmail ? (
                    <div className="h-full flex flex-col">
                      {/* Email Header */}
                      <div className="p-6 border-b border-border bg-background">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-foreground">
                                {selectedEmail?.sender?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">{selectedEmail?.sender}</h3>
                              <p className="text-sm text-muted-foreground">{selectedEmail?.senderEmail}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Button variant="ghost" size="icon">
                              <Icon name="Reply" size={16} />
                            </Button>
                            <Button variant="ghost" size="icon">
                              <Icon name="Forward" size={16} />
                            </Button>
                            <Button variant="ghost" size="icon">
                              <Icon name="Archive" size={16} />
                            </Button>
                            <Button variant="ghost" size="icon">
                              <Icon name="Trash2" size={16} />
                            </Button>
                          </div>
                        </div>
                        
                        <h2 className="text-xl font-semibold text-foreground mb-2">
                          {selectedEmail?.subject}
                        </h2>
                        
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <span>To: you</span>
                          <span>•</span>
                          <span>{selectedEmail?.date?.toLocaleString()}</span>
                          {selectedEmail?.hasAttachment && (
                            <>
                              <span>•</span>
                              <div className="flex items-center space-x-1">
                                <Icon name="Paperclip" size={14} />
                                <span>Attachment</span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {selectedEmail?.labels && selectedEmail?.labels?.length > 0 && (
                          <div className="flex items-center space-x-2 mt-3">
                            {selectedEmail?.labels?.map((label, index) => (
                              <span 
                                key={index}
                                className="px-2 py-1 text-xs font-medium bg-accent/20 text-accent-foreground rounded-full"
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Email Body */}
                      <div className="flex-1 p-6 overflow-y-auto">
                        <div className="prose prose-sm max-w-none">
                          <div className="whitespace-pre-wrap text-foreground">
                            {selectedEmail?.body}
                          </div>
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="p-4 border-t border-border bg-background">
                        <div className="flex items-center space-x-2">
                          <Button variant="default" size="sm">
                            <Icon name="Reply" size={14} className="mr-2" />
                            Reply
                          </Button>
                          <Button variant="outline" size="sm">
                            <Icon name="Forward" size={14} className="mr-2" />
                            Forward
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Icon name="Archive" size={14} className="mr-2" />
                            Archive
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Mail" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">Select an email</h3>
                        <p className="text-muted-foreground">
                          Choose an email from the list to view its contents
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Floating Compose Button */}
        {hasConnectedAccounts && (
          <Button
            variant="default"
            size="lg"
            onClick={() => setIsComposerOpen(true)}
            className="fixed bottom-6 right-6 rounded-full shadow-elevation-2 z-40 lg:hidden"
          >
            <Icon name="Plus" size={20} />
          </Button>
        )}

        {/* Email Composer Modal */}
        <EmailComposer
          isOpen={isComposerOpen}
          onClose={() => setIsComposerOpen(false)}
          onSend={handleSendEmail}
        />
      </main>
    </div>
  );
};

export default EmailsPage;